import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react';
import cn from 'classnames';
import { getTestIds } from '../../utils/TestIds';
import { useBiAction } from '../BiTracking';
import styles from './SegmentControl.module.scss';
export const GetSegmentControlTID = (parentTestId)=>getTestIds(parentTestId);
const HIDDEN_PILL = {
    left: 0,
    width: 0,
    visible: false
};
export function SegmentControl(param) {
    let { options, value, onChange, variant = 'text', className, testId, bi, noBi: _noBi, 'aria-label': ariaLabel } = param;
    const { SegmentControlTID } = useMemo(()=>GetSegmentControlTID(testId), [
        testId
    ]);
    const containerRef = useRef(null);
    const wrapperRef = useRef(null);
    const segmentRefs = useRef([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [pillPosition, setPillPosition] = useState(HIDDEN_PILL);
    const logBiAction = useBiAction();
    const fireBi = useCallback((selectedValue)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'segment_control',
                entityName: bi.name,
                actionType: 'select',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : '',
                    selected_value: String(selectedValue)
                })
            });
        }
    }, [
        bi,
        logBiAction,
        testId
    ]);
    const selectedIndex = options.findIndex((opt)=>opt.value === value);
    const optionCount = options.length;
    const measurePill = useCallback(()=>{
        const wrapper = wrapperRef.current;
        const selectedButton = segmentRefs.current[selectedIndex];
        if (!wrapper || !selectedButton || selectedIndex < 0) {
            setPillPosition((prev)=>prev.visible ? HIDDEN_PILL : prev);
            return;
        }
        const wrapperRect = wrapper.getBoundingClientRect();
        const buttonRect = selectedButton.getBoundingClientRect();
        const next = {
            left: buttonRect.left - wrapperRect.left,
            width: buttonRect.width,
            visible: true
        };
        setPillPosition((prev)=>prev.visible && prev.left === next.left && prev.width === next.width ? prev : next);
    }, [
        selectedIndex
    ]);
    useLayoutEffect(()=>{
        measurePill();
    }, [
        measurePill,
        optionCount,
        value
    ]);
    useEffect(()=>{
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const observer = new ResizeObserver(()=>measurePill());
        observer.observe(wrapper);
        segmentRefs.current.forEach((button)=>{
            if (button) observer.observe(button);
        });
        return ()=>observer.disconnect();
    }, [
        measurePill,
        optionCount
    ]);
    const prevPillVisibleRef = useRef(false);
    const shouldAnimatePill = isInitialized && prevPillVisibleRef.current && pillPosition.visible;
    useEffect(()=>{
        prevPillVisibleRef.current = pillPosition.visible;
    }, [
        pillPosition.visible
    ]);
    useEffect(()=>{
        if (!isInitialized) {
            requestAnimationFrame(()=>{
                setIsInitialized(true);
            });
        }
    }, [
        isInitialized
    ]);
    const handleKeyDown = (event, index)=>{
        let newIndex = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            newIndex = (index + 1) % optionCount;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            newIndex = (index - 1 + optionCount) % optionCount;
        } else if (event.key === 'Home') {
            event.preventDefault();
            newIndex = 0;
        } else if (event.key === 'End') {
            event.preventDefault();
            newIndex = optionCount - 1;
        }
        if (newIndex !== index) {
            var _segmentRefs_current_newIndex;
            fireBi(options[newIndex].value);
            onChange(options[newIndex].value);
            (_segmentRefs_current_newIndex = segmentRefs.current[newIndex]) === null || _segmentRefs_current_newIndex === void 0 ? void 0 : _segmentRefs_current_newIndex.focus();
        }
    };
    return /*#__PURE__*/ _jsx("div", {
        ref: containerRef,
        className: cn(styles.container, className),
        "data-testid": SegmentControlTID.Root(),
        role: "radiogroup",
        "aria-label": ariaLabel,
        children: /*#__PURE__*/ _jsxs("div", {
            ref: wrapperRef,
            className: styles.segmentsWrapper,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.pill, shouldAnimatePill && styles.animated),
                    style: {
                        left: "".concat(pillPosition.left, "px"),
                        width: "".concat(pillPosition.width, "px"),
                        visibility: pillPosition.visible ? 'visible' : 'hidden'
                    },
                    "aria-hidden": "true",
                    role: "presentation"
                }),
                options.map((option, index)=>{
                    const isSelected = option.value === value;
                    const segmentClass = variant === 'icon' ? styles.iconSegment : styles.textSegment;
                    return /*#__PURE__*/ _jsx("button", {
                        ref: (el)=>{
                            segmentRefs.current[index] = el;
                        },
                        type: "button",
                        role: "radio",
                        "aria-checked": isSelected,
                        tabIndex: isSelected ? 0 : -1,
                        className: cn(styles.segment, segmentClass, isSelected && styles.selected),
                        onClick: ()=>{
                            if (option.value !== value) {
                                fireBi(option.value);
                                onChange(option.value);
                            }
                        },
                        onKeyDown: (e)=>handleKeyDown(e, index),
                        "data-testid": option.testId,
                        children: variant === 'icon' ? option.icon : option.label
                    }, option.value);
                })
            ]
        })
    });
}
